/**
 * save/file-io.ts — `.susave` filesystem export / import (v4M1.4).
 *
 * Companion to `save/storage.ts` which handles localStorage slots. This
 * module covers the OS-filesystem round-trip:
 *
 *   exportBlobToFile(blob, filename) — serialise a SaveBlob to a `.susave`
 *     download, surfacing the browser's "Save As" dialog where supported,
 *     falling back to anchor-click download otherwise.
 *
 *   importBlobFromFile() — open a file picker, read the chosen `.susave`,
 *     parse + return the SaveBlob, OR resolve to null when the user
 *     cancels. Schema-version checks happen at deserialise time (caller
 *     passes the blob into `deserializeGameState`); this module is the
 *     transport layer only.
 *
 * `.susave` is just JSON — the extension exists purely to give the OS
 * a meaningful filter on file pickers. No magic bytes, no compression.
 * Saves are typically a few hundred KB so transport overhead doesn't
 * justify gzip; if save bodies grow past ~5 MB, swap in a compressed
 * codec here in one place without touching call sites.
 */

import type { SaveBlob } from './serialize';

/** Mime type used for the download blob. Same as `application/json` semantically. */
const SUSAVE_MIME = 'application/json';

/** Default extension applied to exported files. */
const SUSAVE_EXT = '.susave';

/**
 * Synthesise a default filename from the save blob's metadata.
 * Format: `su-<tick>-<isoDate>.susave` where the date is YYYY-MM-DD and
 * the tick is the sim-tick at save time. The filename is deterministic
 * for a given blob so re-exporting the same save produces the same
 * filename on the user's disk.
 */
export function defaultSusaveFilename(blob: SaveBlob): string {
  const d = new Date(blob.header.savedAt);
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  return `su-${blob.header.tick}-${yyyy}-${mm}-${dd}${SUSAVE_EXT}`;
}

/**
 * Trigger a download of the given SaveBlob as a `.susave` file. Uses
 * the standard anchor-element pattern (create a blob URL, click an
 * anchor with `download` attribute, revoke the URL). Works on every
 * modern browser including those without File System Access API.
 *
 * Browsers may apply their own download-dialog behaviour depending on
 * user settings — that's the OS layer's call, not ours.
 */
export function exportBlobToFile(
  blob: SaveBlob,
  filename: string = defaultSusaveFilename(blob),
): void {
  const json = JSON.stringify(blob);
  const fileBlob = new Blob([json], { type: SUSAVE_MIME });
  const url = URL.createObjectURL(fileBlob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  // Append to DOM so Firefox actually fires the click; some older
  // versions ignore the click on detached anchors. Remove immediately
  // after to avoid leaking a stray element.
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Revoke after a short delay — Safari occasionally needs the URL
  // alive while the download dialog appears. 1 s is well beyond any
  // reasonable dialog latency.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * Open a file picker, let the user choose a `.susave` (or any file —
 * the accept filter is advisory, not enforced), and resolve with the
 * parsed SaveBlob. Resolves to `null` if the user cancels.
 *
 * Validation here is minimal: we parse the JSON and assert it has a
 * `header` and `body` field at the top level so a wildly malformed
 * file rejects early. Deep schema validation (formatVersion, body
 * shape) happens when the caller passes the blob to
 * `deserializeGameState`.
 */
export function importBlobFromFile(): Promise<SaveBlob | null> {
  return new Promise((resolve, reject) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = SUSAVE_EXT + ',application/json';
    input.style.display = 'none';
    // v4M1.4 — listen for both change (file picked) and cancel (where
    // supported). Older browsers fire neither on cancel; that means a
    // cancelled picker leaves the promise pending forever. To handle
    // that, the caller is expected to treat a cancelled import as a
    // no-op (no UI state to revert). Modern Chromium/Firefox fire
    // `cancel` on dismissed dialogs since 2023.
    input.addEventListener('cancel', () => {
      cleanup();
      resolve(null);
    });
    input.addEventListener('change', () => {
      const file = input.files?.[0];
      if (!file) {
        cleanup();
        resolve(null);
        return;
      }
      const reader = new FileReader();
      reader.onerror = () => {
        cleanup();
        reject(new Error('Failed to read file'));
      };
      reader.onload = () => {
        cleanup();
        try {
          const json = String(reader.result ?? '');
          const parsed: unknown = JSON.parse(json);
          if (!isPlausibleSaveBlob(parsed)) {
            reject(new Error('File does not look like a Strategos Universalis save (missing header or body).'));
            return;
          }
          resolve(parsed);
        } catch (err) {
          const msg = err instanceof Error ? err.message : String(err);
          reject(new Error(`Could not parse save file: ${msg}`));
        }
      };
      reader.readAsText(file);
    });
    const cleanup = (): void => {
      if (input.parentNode) input.parentNode.removeChild(input);
    };
    document.body.appendChild(input);
    input.click();
  });
}

/**
 * Cheap shape probe for the imported blob. We rely on
 * `deserializeGameState` for full schema validation; this just
 * rejects files that are obviously not save blobs (random JSON,
 * spreadsheets, screenshots-with-trailing-bytes, etc.).
 */
function isPlausibleSaveBlob(x: unknown): x is SaveBlob {
  if (typeof x !== 'object' || x === null) return false;
  const o = x as Record<string, unknown>;
  if (typeof o.header !== 'object' || o.header === null) return false;
  if (typeof o.body !== 'object' || o.body === null) return false;
  return true;
}
